import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useChainStore } from "../../data/chainStore";
import { useAuth } from "../../data/authStore";

const USER_PREFIX = "user:";

// Lead negotiator options = eligible workspace users (own office first); each saves as that user's staff contact.
export function useLeadNegotiators({ active = true, currentAgentId = null, office = "" } = {}) {
  const { contacts, refreshContacts } = useChainStore();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!active) return undefined;
    let alive = true;
    api.get("/api/team/lead-negotiators", { _suppressErrorToast: true })
      .then(({ data }) => { if (alive) setMembers(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setMembers([]); });
    return () => { alive = false; };
  }, [active]);

  const options = useMemo(() => {
    const officeKey = String(office || "").trim().toLowerCase();
    const myOffice = members.find((m) => m.user_id === user?.id)?.office_id || null;
    const isOwn = (m) => (officeKey
      ? String(m.office_name || "").trim().toLowerCase() === officeKey
      : !!myOffice && m.office_id === myOffice);
    const list = [...members]
      .sort((a, b) => (isOwn(b) - isOwn(a)) || a.full_name.localeCompare(b.full_name))
      .map((m) => ({
        value: m.contact_id || `${USER_PREFIX}${m.user_id}`,
        label: m.full_name,
        hint: m.office_name || m.role_label,
      }));
    // A listing already led by someone outside the team list keeps showing that name.
    if (currentAgentId && !list.some((o) => o.value === currentAgentId)) {
      const c = contacts.find((x) => x.id === currentAgentId);
      if (c) list.unshift({ value: c.id, label: c.name, hint: c.company });
    }
    return list;
  }, [members, contacts, currentAgentId, office, user?.id]);

  const pick = useCallback(async (value) => {
    if (!value) return { contactId: null, officeName: null };
    const member = members.find((m) => m.contact_id === value || `${USER_PREFIX}${m.user_id}` === value);
    if (!String(value).startsWith(USER_PREFIX)) return { contactId: value, officeName: member?.office_name || null };
    const { data } = await api.post(`/api/team/lead-negotiators/${member.user_id}/contact`);
    setMembers((arr) => arr.map((m) => (m.user_id === member.user_id ? { ...m, contact_id: data.id } : m)));
    await refreshContacts();
    return { contactId: data.id, officeName: member.office_name || null };
  }, [members, refreshContacts]);

  return { options, pick };
}
